import React, { useState } from 'react';
import { format } from 'date-fns';

// Date-range picker for the accountant's registers (sales, credit notes).
// The register itself is built server-side from the range alone, so the file
// is the same whatever the list on screen happens to be filtered to.

interface Props {
  label: string;
  /** Short name used in the downloaded file name, e.g. "sales-register". */
  fileSlug: string;
  /** Called with ISO yyyy-MM-dd bounds; resolves to the CSV blob. */
  fetchRegister: (range: { from: string; to: string; includeCancelled: boolean }) => Promise<Blob>;
  title?: string;
}

const iso = (d: Date) => format(d, 'yyyy-MM-dd');

// Indian financial year: 1-Apr to 31-Mar.
function fyStart(d: Date) {
  return new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);
}

const RegisterDownload: React.FC<Props> = ({ label, fileSlug, fetchRegister, title }) => {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(iso(today));
  const [includeCancelled, setIncludeCancelled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const preset = (key: 'month' | 'lastMonth' | 'quarter' | 'fy' | 'lastFy') => {
    const y = today.getFullYear(), m = today.getMonth();
    if (key === 'month') { setFrom(iso(new Date(y, m, 1))); setTo(iso(new Date(y, m + 1, 0))); }
    if (key === 'lastMonth') { setFrom(iso(new Date(y, m - 1, 1))); setTo(iso(new Date(y, m, 0))); }
    if (key === 'quarter') {
      const fy = fyStart(today);
      const q = Math.floor(((y - fy.getFullYear()) * 12 + m - 3) / 3);
      const qs = new Date(fy.getFullYear(), 3 + q * 3, 1);
      setFrom(iso(qs)); setTo(iso(new Date(qs.getFullYear(), qs.getMonth() + 3, 0)));
    }
    if (key === 'fy') { const s = fyStart(today); setFrom(iso(s)); setTo(iso(new Date(s.getFullYear() + 1, 2, 31))); }
    if (key === 'lastFy') { const s = fyStart(today); setFrom(iso(new Date(s.getFullYear() - 1, 3, 1))); setTo(iso(new Date(s.getFullYear(), 2, 31))); }
  };

  const download = async () => {
    setError('');
    if (!from || !to) { setError('Choose both dates'); return; }
    if (from > to) { setError('"From" is after "To"'); return; }
    setBusy(true);
    try {
      const blob = await fetchRegister({ from, to, includeCancelled });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${fileSlug}_${from}_to_${to}.csv`; a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch {
      setError('Download failed — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-secondary text-sm" title={title}>↓ {label}</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
            <div className="flex flex-wrap gap-2">
              {([['month', 'This month'], ['lastMonth', 'Last month'], ['quarter', 'This quarter'], ['fy', 'This FY'], ['lastFy', 'Last FY']] as const).map(([k, t]) => (
                <button key={k} type="button" onClick={() => preset(k)}
                  className="text-xs px-2 py-1 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50">{t}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">From</label>
                <input type="date" className="input-field" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <label className="label">To</label>
                <input type="date" className="input-field" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} />
              Include cancelled documents
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn-primary" disabled={busy} onClick={download}>{busy ? 'Preparing…' : 'Download CSV'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegisterDownload;
